import { Database, LayoutDashboard, Power, PowerOff } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Input } from '@unistocks/components/input/input';
import { Column } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { asyncStateTransition } from '@unistocks/components/query-state/async-state';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { Table } from '@unistocks/components/table/table';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { DataSource, ExternalDataSourceDto } from '@unistocks/proxy/axios-client';
import {
  externalDataSourcesGetListQueryKey,
  useExternalDataSourcesGetListQuery,
  useExternalDataSourcesSetEnabledMutation,
  useExternalDataSourcesSetPriorityMutation
} from '@unistocks/proxy/axios-client/ExternalDataSourcesQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { dataSourceOptions } from '@unistocks/shared/utils/enums/data-source-options';
import React from 'react';
import { View } from 'tamagui';

const getDataSourceName = (dataSource: DataSource) => {
  const option = dataSourceOptions.find(o => o.value === dataSource);

  return option ? t(`::${option.key}`) : String(dataSource);
};

export type DataSourceListRowProps = {
  source: ExternalDataSourceDto;
};

const DataSourceListRow: React.FC<DataSourceListRowProps> = ({ source }) => {
  const { dataSource, isEnabled, priority } = source;
  const queryClient = useQueryClient();

  const { mutate: setEnabled, isPending } = useExternalDataSourcesSetEnabledMutation(dataSource, {
    onSuccess: () => {
      toast.success(
        t(isEnabled ? '::DataSource:Disable:Success' : '::DataSource:Enable:Success', {
          0: getDataSourceName(dataSource)
        })
      );
      queryClient.invalidateQueries({ queryKey: externalDataSourcesGetListQueryKey() });
    },
    onError: () => {
      toast.error(t('::DataSource:Update:Error'));
    }
  });

  const { mutate: setPriority, isPending: isPriorityPending } =
    useExternalDataSourcesSetPriorityMutation(dataSource, {
      onSuccess: () => {
        toast.success(t('::DataSource:Priority:Success', { 0: getDataSourceName(dataSource) }));
        queryClient.invalidateQueries({ queryKey: externalDataSourcesGetListQueryKey() });
      },
      onError: () => {
        toast.error(t('::DataSource:Update:Error'));
      }
    });

  const onToggle = () => {
    setEnabled({ isEnabled: !isEnabled });
  };

  const onCommitPriority = (text: string) => {
    const parsed = Number.parseInt(text, 10);

    if (!Number.isInteger(parsed) || parsed < 1 || parsed === priority) return;

    setPriority({ priority: parsed });
  };

  const priorityText = React.useRef(String(priority));

  return (
    <Table.Row key={dataSource} pressable={false} hoverable={false}>
      <Table.Cell cellLocation="first">
        <Paragraph>{getDataSourceName(dataSource)}</Paragraph>
      </Table.Cell>
      <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }} cellWidth={'$4'}>
        <Input
          size={'$2'}
          px={'$3'}
          contentContainerProps={{ flex: 1 }}
          keyboardType="number-pad"
          defaultValue={priority}
          disabled={isPriorityPending}
          accessibilityLabel={t('::DataSource:Priority:Help')}
          testID={`priority-input-${dataSource}`}
          textAlign="right"
          onChangeText={text => {
            priorityText.current = text;
          }}
          onBlur={() => onCommitPriority(priorityText.current)}
        />
      </Table.Cell>
      <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }} cellWidth={'$4'}>
        <View
          alignSelf="flex-end"
          px={'$2'}
          py={'$1'}
          borderRadius={'$2'}
          bg={isEnabled ? '$green8' : '$gray8'}
        >
          <Paragraph color={'$white1'} size={'$3'}>
            {t(isEnabled ? '::DataSource:Enabled' : '::DataSource:Disabled')}
          </Paragraph>
        </View>
      </Table.Cell>
      <Table.ActionsCell cellWidth={'$6'}>
        <Confirm
          title={t(isEnabled ? '::DataSource:Disable' : '::DataSource:Enable')}
          message={t(isEnabled ? '::DataSource:Disable:Confirm' : '::DataSource:Enable:Confirm', {
            0: getDataSourceName(dataSource)
          })}
          confirmText={t(isEnabled ? '::DataSource:Disable' : '::DataSource:Enable')}
          onConfirm={onToggle}
          isLoading={isPending}
        >
          <Button
            size={'$2'}
            variant={'outlined'}
            Icon={isEnabled ? PowerOff : Power}
            loading={isPending}
          >
            {t(isEnabled ? '::DataSource:Disable' : '::DataSource:Enable')}
          </Button>
        </Confirm>
      </Table.ActionsCell>
    </Table.Row>
  );
};

DataSourceListRow.displayName = 'DataSourceListRow';

export const DataSourceList: React.FC = () => {
  const { data, isSuccess, isRefetching, refetch } =
    useExternalDataSourcesGetListQuery();

  return (
    <Page>
      <Page.ScrollView
        refreshing={isRefetching}
        onRefresh={refetch}
        headerTitle={t('::Menu:DataSources')}
        breadcrumbs={[
          {
            key: 'admin',
            href: '/(admin)',
            name: t('::Menu:Administration'),
            rootPathName: 'admin',
            Icon: props => <LayoutDashboard {...props} />
          }
        ]}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Column mt={'$3'}>
          <Table
            transition="quickLessBouncy"
            animateOnly={['opacity', 'transform']}
            enterStyle={{ o: 0, y: -20 }}
          >
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell cellLocation="first">
                  <Table.HeaderCellText>{t('::DataSource')}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="middle"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$4'}
                >
                  <Table.HeaderCellText>{t('::DataSource:Priority')}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="middle"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$4'}
                >
                  <Table.HeaderCellText>{t('::DataSource:Status')}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="last"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$6'}
                >
                  <Table.HeaderCellText>{t('AbpUi::Actions')}</Table.HeaderCellText>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>

            <QueryState
              queryKeys={[externalDataSourcesGetListQueryKey()]}
              isEmpty={isSuccess && !data.items.length}
              noDataIcon={Database}
            >
              <Table.Body key={'table-body'} {...asyncStateTransition}>
                {isSuccess &&
                  data.items.map(source => (
                    <DataSourceListRow key={source.dataSource} source={source} />
                  ))}
              </Table.Body>
            </QueryState>
          </Table>
        </Column>
      </Page.ScrollView>
    </Page>
  );
};

DataSourceList.displayName = 'DataSourceList';
